import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CreditCard, Save, AlertTriangle, Loader2 } from "lucide-react";
import { getPaymentSettings, savePaymentSettings } from "@/lib/api/finance.functions";

export const Route = createFileRoute("/_authenticated/m/cadastro-formas-pagamento")({
  component: Page,
});

const METHODS = [
  { id: "dinheiro", label: "Dinheiro (Presencial)", requiresMp: false },
  { id: "pix", label: "Pix (Automático)", requiresMp: true },
  { id: "pix_comprovante", label: "Pix (Enviar comprovante)", requiresMp: false },
  { id: "cartao_presencial", label: "Cartão de Crédito (Presencial)", requiresMp: false },
  { id: "cartao_credito", label: "Cartão de Crédito (Automático)", requiresMp: true },
];

function Page() {
  const getFn = useServerFn(getPaymentSettings);
  const saveFn = useServerFn(savePaymentSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState<string[]>([]);
  const [mpAccess, setMpAccess] = useState("");
  const [mpPublic, setMpPublic] = useState("");
  const [hasMpToken, setHasMpToken] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await getFn();
        setDefaults(r.defaults ?? []);
        setHasMpToken(r.hasMpToken);
        setMpPublic(r.mpPublicKey ?? "");
      } catch (e: any) {
        toast.error(e?.message ?? "Erro ao carregar");
      } finally { setLoading(false); }
    })();
  }, []);

  const toggle = (id: string, checked: boolean) =>
    setDefaults((prev) => (checked ? [...prev, id] : prev.filter((m) => m !== id)));

  const save = async () => {
    setSaving(true);
    try {
      await saveFn({ data: {
        defaults,
        mp_access_token: mpAccess.trim() ? mpAccess.trim() : undefined,
        mp_public_key: mpPublic.trim() ? mpPublic.trim() : undefined,
      }});
      if (mpAccess.trim()) setHasMpToken(true);
      setMpAccess("");
      toast.success("Configurações salvas");
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border p-6 flex items-start gap-4">
        <div className="rounded-xl bg-primary/15 p-3 text-primary"><CreditCard className="h-7 w-7" /></div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Formas de Pagamento</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Configure as formas de pagamento padrão aceitas pelo seu Centro de Treinamento.
            Elas serão oferecidas automaticamente em novas vendas e cadastros.
          </p>
        </div>
      </div>

      {!hasMpToken && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Mercado Pago não configurado</AlertTitle>
          <AlertDescription>
            As opções automáticas (Pix Automático, Cartão de Crédito Automático)
            ficam desabilitadas até você informar o <strong>Access Token</strong> do Mercado Pago abaixo.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Métodos Aceitos e Padrões</CardTitle>
          <CardDescription>Marque as opções que você utiliza no dia a dia para recebimentos.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2">
            {METHODS.map((m) => {
              const disabled = m.requiresMp && !hasMpToken;
              const active = defaults.includes(m.id);
              return (
                <div key={m.id}
                  className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                    disabled ? "bg-muted/40 opacity-60" : active ? "bg-primary/5 border-primary/40" : "hover:bg-secondary/30"
                  }`}>
                  <div>
                    <p className="font-medium">{m.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {disabled ? "Requer integração Mercado Pago" : active ? "Ativo" : "Inativo"}
                    </p>
                  </div>
                  <Switch disabled={disabled} checked={active}
                    onCheckedChange={(c) => toggle(m.id, c)} />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Integração Mercado Pago</CardTitle>
          <CardDescription>
            Necessário para Pix automático e Cartão de Crédito.
            {hasMpToken && <span className="ml-1 text-green-600 font-medium">✓ Token configurado</span>}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Access Token (privado)</Label>
            <Input type="password" placeholder={hasMpToken ? "•••••••• (preencher para substituir)" : "APP_USR-..."}
              value={mpAccess} onChange={(e) => setMpAccess(e.target.value)} />
          </div>
          <div>
            <Label>Public Key (opcional)</Label>
            <Input placeholder="APP_USR-..." value={mpPublic} onChange={(e) => setMpPublic(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <div>
        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salvar Configurações
        </Button>
      </div>
    </div>
  );
}
